import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { PageHeader, StatCard } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Plus, AlertTriangle, Search, Edit3 } from "lucide-react";
import { toast } from "sonner";

export default function BuyerInventory() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [edit, setEdit] = useState(null);
  const [newOnHand, setNewOnHand] = useState(0);

  const load = () => api.get("/inventory").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const update = async () => {
    await api.patch(`/inventory/${edit.id}`, { on_hand: Number(newOnHand) });
    toast.success("Stock updated");
    setEdit(null); load();
  };

  const filtered = items.filter(i => (!search || i.name.toLowerCase().includes(search.toLowerCase())) &&
    (filter === "all" || (filter === "low" && i.on_hand <= i.reorder_point) || (filter === "ok" && i.on_hand > i.reorder_point)));

  const total = items.length;
  const lowStock = items.filter(i => i.on_hand <= i.reorder_point).length;
  const outOfStock = items.filter(i => i.on_hand === 0).length;

  return (
    <>
      <PageHeader title="Inventory & Warehousing" subtitle="Stock levels, reorder alerts, multi-warehouse"
        action={<Button onClick={()=>nav("/buyer/catalog")} className="rounded-full bg-foreground text-background hover:bg-foreground/90"><Plus className="w-4 h-4 mr-1"/>Add stock</Button>}/>
      <div className="p-8 space-y-5">
        <div className="grid md:grid-cols-4 gap-4">
          <StatCard label="Total SKUs" value={total} hint="across 1 warehouse" tone="ink"/>
          <StatCard label="Low stock" value={lowStock} hint="below reorder point"/>
          <StatCard label="Out of stock" value={outOfStock} hint="needs immediate reorder"/>
          <StatCard label="Healthy" value={total - lowStock} hint="above reorder point"/>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex rounded-full border border-border bg-white p-1">
            {["all", "low", "ok"].map(f => (
              <button key={f} onClick={()=>setFilter(f)} data-testid={`inv-${f}`}
                className={`px-4 py-1.5 rounded-full text-sm capitalize ${filter===f?"bg-foreground text-background":"text-foreground/60 hover:text-foreground"}`}>
                {f === "all" ? "All" : f === "low" ? `Low (${lowStock})` : `Healthy`}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
            <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search SKU or name…" className="pl-9 h-10 w-64"/>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-widest text-foreground/60">
              <tr>
                <th className="px-5 py-3">Product</th>
                <th className="px-5 py-3">SKU</th>
                <th className="px-5 py-3">Warehouse</th>
                <th className="px-5 py-3">On hand</th>
                <th className="px-5 py-3">Reorder pt</th>
                <th className="px-5 py-3">Reorder qty</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(i => {
                const low = i.on_hand <= i.reorder_point;
                const critical = i.on_hand <= i.reorder_point / 2;
                return (
                  <tr key={i.id} data-testid={`inv-${i.sku}`}>
                    <td className="px-5 py-4 font-medium">{i.name}</td>
                    <td className="px-5 py-4 mono text-foreground/60">{i.sku}</td>
                    <td className="px-5 py-4 text-foreground/70 text-xs">{i.warehouse}</td>
                    <td className="px-5 py-4 display text-lg">{i.on_hand}</td>
                    <td className="px-5 py-4 text-foreground/60">{i.reorder_point}</td>
                    <td className="px-5 py-4 text-foreground/60">{i.reorder_qty}</td>
                    <td className="px-5 py-4">
                      {critical ? <span className="chip bg-rose-100 text-rose-700"><AlertTriangle className="w-3 h-3"/>Critical</span> :
                       low ? <span className="chip bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3"/>Low</span> :
                       <span className="chip bg-accent/30 border border-accent/40">Healthy</span>}
                    </td>
                    <td className="px-5 py-4 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={()=>{setEdit(i); setNewOnHand(i.on_hand);}} data-testid={`edit-${i.sku}`}><Edit3 className="w-3.5 h-3.5"/></Button>
                      <Button size="sm" onClick={()=>nav("/buyer/catalog")} data-testid={`reorder-${i.sku}`} className="rounded-full bg-foreground text-background hover:bg-foreground/90">Reorder</Button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length===0 && <tr><td colSpan={8} className="px-5 py-12 text-center text-foreground/60">No items match</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(v)=>!v && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust stock — {edit?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-xl bg-secondary/40 p-4 text-sm">
              <div className="flex justify-between"><span className="text-foreground/60">SKU</span><span className="mono">{edit?.sku}</span></div>
              <div className="flex justify-between"><span className="text-foreground/60">Reorder point</span><span>{edit?.reorder_point}</span></div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-foreground/60">New on-hand</label>
              <Input type="number" value={newOnHand} onChange={e=>setNewOnHand(e.target.value)} className="mt-2 h-11" data-testid="adj-input"/>
            </div>
            <Button onClick={update} className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="adj-save">Save adjustment</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
